"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  SankeyDiagram,
  type SankeyLinkColor,
} from "@/components/tools/diagrams-more/sankey-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { diagramColor } from "@/lib/tool-kit/diagrams/color";
import {
  layoutSankey,
  parseSankey,
  SANKEY_LINK_LIMIT,
  SANKEY_NODE_LIMIT,
  type SankeyOrder,
  type SankeyWarning,
  sankeyRows,
  unbalancedNodes,
} from "@/lib/tool-kit/diagrams-more/sankey";

/** English strings. A translated content file overrides these through `ui`. */
export const SANKEY_DIAGRAM_MAKER_STRINGS = {
  inputLabel: "Your flows — one per line: Source -> Target: value",
  inputHint:
    "Write Wages -> Household income: 3400. The arrow can be ->, → or =>, and the SankeyMATIC form Wages [3400] Household income works too. Repeat lines add up. Start a line with # for a comment. Each name is one box wherever it appears; a flow that would loop back is left out.",
  exampleText:
    "# Where a household's monthly income goes\nWages -> Household income: 3400\nBenefits -> Household income: 300\nSide work -> Household income: 500\nHousehold income -> Housing: 1500\nHousehold income -> Food: 700\nHousehold income -> Transport: 450\nHousehold income -> Savings: 600\nHousehold income -> Other: 950\nHousing -> Rent: 1250\nHousing -> Utilities: 250\nFood -> Groceries: 560\nFood -> Eating out: 140\nTransport -> Fuel: 220\nTransport -> Public transit: 230",
  exampleTitle: "Monthly Household Budget (USD)",
  loadExample: "Load the example",
  clear: "Clear",
  orderLabel: "Order of the boxes in each column",
  orderCrossings: "Reduce crossings",
  orderInput: "Order of first appearance",
  linkColorLabel: "Color the ribbons by",
  linkSource: "Their source",
  linkTarget: "Their target",
  linkGrey: "Gray",
  showValues: "Show the amounts in the labels",
  unitLabel: "Unit shown after each amount (optional)",
  unitPlaceholder: "e.g. GWh",
  nodesDetected: "one:{count} box|other:{count} boxes",
  flowsDetected: "one:{count} flow|other:{count} flows",
  columnsDetected: "one:{count} column|other:{count} columns",
  enterFlows:
    "Type at least one flow, such as Wages -> Income: 3400, to draw the diagram.",
  unbalanced:
    "These boxes take in and send out different amounts, so flow appears or vanishes there: {names}.",
  warnings: {
    badLine:
      "Line {line}: “{text}” is not in the form Source -> Target: value, so it was left out.",
    badValue:
      "Line {line}: the value for {text} must be a number above zero, so it was left out.",
    selfLink: "Line {line}: “{text}” links to itself, so it was left out.",
    cycle:
      "Line {line}: {text} would make a loop and a Sankey diagram flows one way, so it was left out.",
    tooMany:
      "Line {line}: {text} was left out — the diagram holds up to {nodes} boxes and {links} flows.",
  },
  moreWarnings:
    "one:…and {count} more notice.|other:…and {count} more notices.",
  fromColumn: "From",
  toColumn: "To",
  valueColumn: "Amount",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  firstColor: "Color of the first box (the rest follow in order)",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (optional)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy flows as a table",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the flows as a table",
  chartLabel: "Sankey diagram",
  chartDescription:
    "Sankey diagram with {nodes} and {flows} across {columns}. The largest flow is {from} to {to}, {value}.",
} as const;

const SHOWN_WARNINGS = 4;

export default function SankeyDiagramMakerWidget() {
  const strings = useToolUi(SANKEY_DIAGRAM_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [order, setOrder] = useState<SankeyOrder>("crossings");
  const [linkColor, setLinkColor] = useState<SankeyLinkColor>("source");
  const [showValues, setShowValues] = useState(true);
  const [unit, setUnit] = useState("");
  const [colorId, setColorId] = useState<ChartColorId>("blue");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseSankey(raw);
  const amount = (value: number): string => {
    const text = fmt(value, { maximumFractionDigits: 2 });
    return unit.trim() === "" ? text : `${text} ${unit.trim()}`;
  };
  const layout =
    parse.links.length > 0
      ? layoutSankey(parse, order, showValues ? amount : null)
      : null;
  const start = colorIndex(colorId);
  const nodeColors = parse.nodes.map((node) => diagramColor(start, node.id));
  const rows = sankeyRows(parse);
  const shown = parse.warnings.slice(0, SHOWN_WARNINGS);
  const hidden = parse.warnings.length - shown.length;
  const imbalance = unbalancedNodes(parse);
  const nodesText = tPlural(strings.nodesDetected, locale, parse.nodes.length, {
    count: fmt(parse.nodes.length),
  });
  const flowsText = tPlural(strings.flowsDetected, locale, parse.links.length, {
    count: fmt(parse.links.length),
  });
  const columnsText = layout
    ? tPlural(strings.columnsDetected, locale, layout.columns, {
        count: fmt(layout.columns),
      })
    : "";
  const largest = rows.reduce(
    (best, row) => (row.value > best.value ? row : best),
    rows[0] ?? { source: "", target: "", value: 0 }
  );

  function warningText(warning: SankeyWarning): string {
    return t(strings.warnings[warning.kind], {
      line: fmt(warning.line),
      text: warning.text,
      nodes: fmt(SANKEY_NODE_LIMIT),
      links: fmt(SANKEY_LINK_LIMIT),
    });
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="sdm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        rows={14}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {nodesText}
            {" · "}
            {flowsText}
            {" · "}
            {columnsText}
          </p>
        ) : (
          <Notice>{strings.enterFlows}</Notice>
        )}
        {imbalance.length > 0 ? (
          <Notice>
            {t(strings.unbalanced, { names: imbalance.join(", ") })}
          </Notice>
        ) : null}
        {shown.map((warning) => (
          <Notice key={`${warning.kind}-${warning.line}-${warning.text}`}>
            {warningText(warning)}
          </Notice>
        ))}
        {hidden > 0 ? (
          <p className={HINT}>
            {tPlural(strings.moreWarnings, locale, hidden, {
              count: fmt(hidden),
            })}
          </p>
        ) : null}
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <PillGroup
          label={strings.orderLabel}
          name="sdm-order"
          onChange={setOrder}
          options={[
            { value: "crossings", label: strings.orderCrossings },
            { value: "input", label: strings.orderInput },
          ]}
          value={order}
        />
        <PillGroup
          label={strings.linkColorLabel}
          name="sdm-link"
          onChange={setLinkColor}
          options={[
            { value: "source", label: strings.linkSource },
            { value: "target", label: strings.linkTarget },
            { value: "grey", label: strings.linkGrey },
          ]}
          value={linkColor}
        />
      </div>
      <div className="grid items-end gap-4 sm:grid-cols-2">
        <Checkbox
          checked={showValues}
          label={strings.showValues}
          onChange={setShowValues}
        />
        <TextField
          id="sdm-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          placeholder={strings.unitPlaceholder}
          value={unit}
        />
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([
            [strings.fromColumn, strings.toColumn, strings.valueColumn],
            ...rows.map((row) => [row.source, row.target, row.value]),
          ])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "sankey-diagram")}
          labels={strings}
          note={style.note}
          table={{
            head: [strings.fromColumn, strings.toColumn, strings.valueColumn],
            rows: rows.map((row) => [
              row.source,
              row.target,
              amount(row.value),
            ]),
          }}
          title={style.title}
        >
          <SankeyDiagram
            description={t(strings.chartDescription, {
              nodes: nodesText,
              flows: flowsText,
              columns: columnsText,
              from: largest.source,
              to: largest.target,
              value: amount(largest.value),
            })}
            id="sdm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            linkColor={linkColor}
            nodeColors={nodeColors}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="sdm"
        labels={strings}
        style={style}
        update={update}
      >
        <ColorSwatches
          labels={strings}
          legend={strings.firstColor}
          name="sdm-color"
          onChange={setColorId}
          value={colorId}
        />
      </ChartStyleFields>
    </div>
  );
}
